import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import bgImage from "../assets/bg.png";
import logo from "../assets/siigmatic-logo.png";
import axiosInstance from "../utils/axiosInstance";
import { clearCurrentUserCache, getCurrentUser } from "../utils/authClient";
import { ArrowBigLeft } from "lucide-react";
import { SKIP_AUTH, getDevAuthUser } from "../utils/authMode";

const SignIn = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [step, setStep] = useState("email"); // email | code
    const [loading, setLoading] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (SKIP_AUTH) {
            const data = getDevAuthUser();
            localStorage.setItem("email", data.email);
            localStorage.setItem("username", data.username);
            localStorage.setItem("role", data.role);
            navigate("/dashboard", { replace: true });
            return;
        }

        const checkExistingSession = async () => {
            const data = await getCurrentUser();
            if (data?.email) {
                localStorage.setItem("email", data.email);
                if (data.username) {
                    localStorage.setItem("username", data.username);
                }
                navigate("/dashboard");
                return;
            }
            setCheckingSession(false);
        };

        checkExistingSession();
    }, [navigate]);

    const handleRequestCode = async () => {
        setError("");
        setMessage("");

        const trimmedEmail = email.trim().toLowerCase();

        if (!trimmedEmail) {
            setError("Please enter your email.");
            return;
        }

        try {
            setLoading(true);

            const { data } = await axiosInstance.post("/user/request-code", {
                email: trimmedEmail,
            });

            setEmail(trimmedEmail);
            setStep("code");
            if (data?.debug_code) {
                setCode(String(data.debug_code));
                setMessage(
                    `${data?.message || "Code sent. Please check your email."} Debug code: ${data.debug_code}`
                );
            } else {
                setMessage(data?.message || "Code sent. Please check your email.");
            }
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to request code."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        setError("");
        setMessage("");

        if (!code.trim()) {
            setError("Please enter the code.");
            return;
        }

        try {
            setLoading(true);

            const { data } = await axiosInstance.post("/user/verify-code", {
                email: email.trim().toLowerCase(),
                code: code.trim(),
            });

            if (data?.user?.email) {
                localStorage.setItem("email", data.user.email);
            }

            if (data?.user?.username) {
                localStorage.setItem("username", data.user.username);
            }
            clearCurrentUserCache();

            setMessage(data?.message || "Login successful.");
            navigate("/dashboard");
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to verify code."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setStep("email");
        setCode("");
        setError("");
        setMessage("");
    };

    if (checkingSession) {
        return (
            <div
                className="min-h-[100svh] flex items-center justify-center bg-gray-100 px-4 bg-cover bg-center"
                style={{ backgroundImage: `url(${bgImage})` }}
            >
                <div className="w-full max-w-xl">
                    <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-black/70 backdrop-blur-sm shadow-[0_18px_45px_rgba(15,23,42,0.25)]">
                        <div className="p-6 sm:p-8">
                            <p className="text-sm text-gray-200">Checking session...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-[100svh] flex items-center justify-center bg-gray-100 px-4 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgImage})` }}
        >
            <div className="w-full max-w-xl">
                <div className="relative overflow-hidden rounded-2xl border border-gray-200/70 bg-black/70 backdrop-blur-sm shadow-[0_18px_45px_rgba(15,23,42,0.25)]">
                    <button
                        onClick={() => navigate("/")}
                        className="absolute left-6 top-4 z-10 inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-md backdrop-blur-sm transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 active:scale-[0.99]"
                    >
                        <ArrowBigLeft size={16} />
                        Back to Home
                    </button>
                    <div className="p-6 pt-14 sm:p-6 sm:pt-16 space-y-8">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-44 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
                                <img
                                    src={logo}
                                    alt="SiiGMATIC logo"
                                    className="h-10 w-36 object-contain"
                                />
                            </div>
                            <div className="flex flex-col text-left">
                                <span className="text-sm font-semibold uppercase tracking-wide text-yellow-200">
                                    Engineering Assistant
                                </span>
                                <span className="text-xs text-gray-300">
                                    Helpful engineering platform
                                </span>
                            </div>
                        </div>

                        <div className="space-y-2 text-left">
                            <h1 className="text-2xl sm:text-3xl font-semibold text-white">
                                Welcome to Engineering Assistant
                            </h1>
                            <p className="text-sm sm:text-base text-gray-200">
                                Sign in with your email and one-time code.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-200">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    disabled={loading || step === "code"}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    className="h-11 w-full rounded-lg border border-gray-500/70 bg-white/10 px-4 text-sm text-white placeholder:text-gray-400 outline-none transition focus:border-yellow-400"
                                />
                            </div>

                            {step === "code" && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-200">
                                        Login code
                                    </label>
                                    <input
                                        type="text"
                                        value={code}
                                        disabled={loading}
                                        onChange={(e) => setCode(e.target.value)}
                                        placeholder="Enter 6-digit code"
                                        className="h-11 w-full rounded-lg border border-gray-500/70 bg-white/10 px-4 text-sm text-white placeholder:text-gray-400 outline-none transition focus:border-yellow-400"
                                    />
                                </div>
                            )}

                            {error && (
                                <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                                    {error}
                                </div>
                            )}

                            {message && (
                                <div className="rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                                    {message}
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-3">
                                {step === "email" ? (
                                    <button
                                        onClick={handleRequestCode}
                                        type="button"
                                        disabled={loading}
                                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-yellow-500 px-4 py-3 text-sm font-semibold text-slate-900 shadow-md transition hover:bg-yellow-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/70 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {loading ? "Sending..." : "Send login code"}
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            onClick={handleVerifyCode}
                                            type="button"
                                            disabled={loading}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-yellow-500 px-4 py-3 text-sm font-semibold text-slate-900 shadow-md transition hover:bg-yellow-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/70 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {loading ? "Verifying..." : "Verify and sign in"}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleBack}
                                            disabled={loading}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-500/70 bg-transparent px-4 py-3 text-sm font-semibold text-gray-200 shadow-sm transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            Change email
                                        </button>
                                    </>
                                )}
                            </div>

                            <p className="text-xs text-gray-300 text-center">
                                Secure sign-in with a one-time code and server-backed session.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignIn;
